import { useEffect, useMemo, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import { ASSIST_WARN_BEFORE_MS, type AssistMirror, type KioskAssistCall, type Locale } from '@360face/domain';
import type { HubApi } from '../../api/client';
import { ASSIST_MESSAGES, assistView, mmss, prettyPhone, secondsLeft, whatsappLink } from './assist';

const POLL_MS = 2_000;
const ENDED_CLOSE_MS = 3_000;

/**
 * Kiosk "Call staff" as a video call with the operator (owner 20.10.2026). Rings every online
 * operator; after 30 s the studio tablets get a red alert; after 60 s (or with nobody online) the
 * studio WhatsApp QR appears with "Try again". Mock-free: if the server has no video set up, the
 * overlay shows the WhatsApp fallback straight away.
 */
export function AssistCall({
  api,
  locationId,
  locale,
  screen,
  mirror,
  onClose,
}: {
  api: HubApi;
  locationId: string;
  locale: Locale;
  screen: string;
  mirror: AssistMirror;
  onClose: () => void;
}) {
  const t = ASSIST_MESSAGES[locale];
  const [attempt, setAttempt] = useState(0);
  const [call, setCall] = useState<KioskAssistCall>();
  const [whatsapp, setWhatsapp] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const secret = useRef<string>();
  const live = useRef<KioskAssistCall>();
  live.current = call;

  // Start (and restart on "Try again").
  useEffect(() => {
    let stop = false;
    setCall(undefined);
    setFailed(false);
    secret.current = undefined;
    api.assistStart(locationId, screen, locale).then(
      (c) => {
        if (stop) {
          if (c.secret) api.assistEnd(locationId, c.id, c.secret).catch(() => undefined);
          return;
        }
        secret.current = c.secret;
        if (c.whatsapp) setWhatsapp(c.whatsapp);
        setCall(c);
      },
      () => !stop && setFailed(true),
    );
    return () => {
      stop = true;
    };
  }, [api, locationId, screen, locale, attempt]);

  // Poll while ringing or connected.
  const status = call?.status;
  const id = call?.id;
  useEffect(() => {
    if (!id || (status !== 'ringing' && status !== 'active')) return;
    const timer = window.setInterval(() => {
      const s = secret.current;
      if (!s) return;
      api.assistGet(locationId, id, s).then(
        (c) => {
          if (c.whatsapp) setWhatsapp(c.whatsapp);
          setCall((prev) => (prev?.id === c.id ? c : prev));
        },
        () => undefined, // keep the last state; the next poll retries (GET is safe)
      );
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [api, locationId, id, status]);

  // Clock for the limit countdown.
  useEffect(() => {
    if (status !== 'active') return;
    const t1 = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t1);
  }, [status]);

  // The operator sees the screen only while connected; send it when it changes.
  const mirrorJson = JSON.stringify(mirror);
  useEffect(() => {
    const s = secret.current;
    if (status !== 'active' || !id || !s) return;
    api.assistMirror(locationId, id, s, JSON.parse(mirrorJson) as AssistMirror).catch(() => undefined);
  }, [api, locationId, id, status, mirrorJson]);

  // Leaving the kiosk (reset, close) ends a ringing or connected call.
  useEffect(
    () => () => {
      const c = live.current;
      const s = secret.current;
      if (c && s && (c.status === 'ringing' || c.status === 'active')) {
        api.assistEnd(locationId, c.id, s).catch(() => undefined);
      }
    },
    [api, locationId],
  );

  const view = assistView(call, failed);
  useEffect(() => {
    if (view.kind !== 'ended') return;
    const t2 = window.setTimeout(onClose, ENDED_CLOSE_MS);
    return () => window.clearTimeout(t2);
  }, [view.kind, onClose]);

  const left = view.kind === 'active' ? secondsLeft(call?.endsAt, now) : undefined;
  const warn = left !== undefined && left * 1000 <= ASSIST_WARN_BEFORE_MS;

  const end = () => {
    const c = live.current;
    const s = secret.current;
    if (c && s && (c.status === 'ringing' || c.status === 'active')) {
      api.assistEnd(locationId, c.id, s).catch(() => undefined);
    }
    secret.current = undefined;
    onClose();
  };

  return (
    <div className="v2-backdrop v2-assist-backdrop">
      <div
        className={view.kind === 'active' ? 'v2-sheet v2-assist v2-assist-live' : 'v2-sheet v2-assist'}
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
      >
        {view.kind === 'active' ? (
          <>
            <div className="v2-assist-head">
              <strong>{t.connected(view.operatorName)}</strong>
              {left !== undefined && (
                <span className={warn ? 'v2-assist-timer warn' : 'v2-assist-timer'} role={warn ? 'alert' : undefined}>
                  {warn ? t.endsIn(mmss(left)) : mmss(left)}
                </span>
              )}
            </div>
            <iframe className="v2-assist-video" title={t.title} src={view.src} allow="camera; microphone; autoplay" />
            <button className="v2-btn v2-btn-red" onClick={end}>
              {t.end}
            </button>
          </>
        ) : view.kind === 'fallback' ? (
          <>
            <h2>{t.fallbackTitle}</h2>
            {whatsapp ? (
              <>
                <p>{t.fallbackSub}</p>
                <WhatsAppQr phone={whatsapp} />
              </>
            ) : null}
            <div className="v2-sheet-grid">
              <button className="v2-btn v2-btn-red" onClick={() => setAttempt((a) => a + 1)}>
                {t.retry}
              </button>
              <button className="v2-btn v2-btn-quiet" onClick={end}>
                {t.close}
              </button>
            </div>
          </>
        ) : view.kind === 'ended' ? (
          <>
            <h2>{t.ended}</h2>
            <button className="v2-btn v2-btn-quiet" onClick={onClose}>
              {t.close}
            </button>
          </>
        ) : (
          <>
            <h2>{t.title}</h2>
            <div className="v2-assist-ring" aria-hidden />
            <p role="status">
              <strong>{t.calling}</strong>
              <br />
              {view.kind === 'ringing' && view.stage === 'tablets' ? t.tablets : t.callingSub}
            </p>
            <p className="v2-assist-small">{t.camera}</p>
            <button className="v2-btn v2-btn-quiet" onClick={end}>
              {t.cancel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function WhatsAppQr({ phone }: { phone: string }) {
  const svg = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(whatsappLink(phone));
    q.make();
    return q.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
  }, [phone]);
  return (
    <figure className="v2-assist-qr">
      {/* SVG built locally from our own studio number */}
      <div dangerouslySetInnerHTML={{ __html: svg }} />
      <figcaption>WhatsApp {prettyPhone(phone)}</figcaption>
    </figure>
  );
}
